import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Upload, Instagram, User, FileText, X, CheckCircle, Tag, Save, ArrowLeft, BarChart3 } from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ModelEdit = () => {
  const { modelId, editToken } = useParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  
  const [model, setModel] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  
  const [formData, setFormData] = useState({
    name: '',
    instagram_url: '',
    bio: ''
  });
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [availableCategories, setAvailableCategories] = useState([]);
  const [newImages, setNewImages] = useState([]);
  const [newPreviews, setNewPreviews] = useState([]);

  useEffect(() => {
    fetchModel();
    fetchCategories();
  }, [modelId, editToken]);

  const fetchModel = async () => {
    try {
      const response = await axios.get(`${API}/models/${modelId}/edit/${editToken}`);
      const modelData = response.data;
      setModel(modelData);
      setFormData({
        name: modelData.name || '',
        instagram_url: modelData.instagram_url || '',
        bio: modelData.bio || ''
      });
      setSelectedCategories(modelData.categories || []);
    } catch (error) {
      console.error('Error fetching model:', error);
      setError('Invalid edit link or model not found. Please check your URL.');
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${API}/models/categories`);
      setAvailableCategories(response.data.categories || []);
    } catch (error) {
      setAvailableCategories(['Fashion', 'Boudoir', 'Editorial', 'Commercial', 'Fitness', 'Alternative', 'Glamour', 'Art Nude']);
    }
  };

  const toggleCategory = (category) => {
    setSelectedCategories(prev => 
      prev.includes(category) 
        ? prev.filter(c => c !== category)
        : [...prev, category]
    );
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleImageChange = (e) => {
    const files = Array.from(e.target.files);
    const currentTotal = (model?.image_urls?.length || 0) + newImages.length;
    
    if (files.length + currentTotal > 4) {
      toast({
        title: "Too many images",
        description: "You can have a maximum of 4 images total.",
        variant: "destructive",
      });
      return;
    }

    const validFiles = files.filter(file => file.type.startsWith('image/'));
    setNewImages(prev => [...prev, ...validFiles]);
    
    validFiles.forEach(file => {
      const reader = new FileReader();
      reader.onloadend = () => {
        setNewPreviews(prev => [...prev, reader.result]);
      };
      reader.readAsDataURL(file);
    });
  };

  const removeNewImage = (index) => {
    setNewImages(prev => prev.filter((_, i) => i !== index));
    setNewPreviews(prev => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      const formDataToSend = new FormData();
      formDataToSend.append('name', formData.name);
      formDataToSend.append('instagram_url', formData.instagram_url);
      formDataToSend.append('bio', formData.bio);
      formDataToSend.append('categories', selectedCategories.join(','));
      
      newImages.forEach((image) => {
        formDataToSend.append('images', image);
      });

      await axios.patch(`${API}/models/${modelId}/edit/${editToken}`, formDataToSend, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      toast({
        title: "Profile Updated!",
        description: "Your changes have been saved successfully.",
      });

      // Refresh data
      await fetchModel();
      setNewImages([]);
      setNewPreviews([]);

    } catch (error) {
      console.error('Error updating profile:', error);
      toast({
        title: "Update Failed",
        description: error.response?.data?.detail || "Failed to update profile. Please try again.",
        variant: "destructive",
      });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="bg-black text-white min-h-screen pt-32 pb-20 flex justify-center items-center">
        <div className="w-8 h-8 border-2 border-red-500 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-black text-white min-h-screen pt-32 pb-20">
        <div className="container mx-auto px-4 text-center">
          <h1 className="text-3xl font-bold text-red-500 mb-4">Access Denied</h1>
          <p className="text-gray-400 mb-8">{error}</p>
          <a 
            href="/"
            className="inline-flex items-center gap-2 text-red-400 hover:text-red-300 transition-colors"
          >
            <ArrowLeft size={18} /> Return Home
          </a>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-black text-white min-h-screen pt-32 pb-20">
      <div className="container mx-auto px-4 lg:px-8">
        {/* Header */}
        <div className="max-w-2xl mx-auto">
          <button 
            onClick={() => navigate(-1)}
            className="inline-flex items-center gap-2 text-gray-400 hover:text-red-400 transition-colors mb-6"
          >
            <ArrowLeft size={18} /> Back
          </button>
          
          <h1 className="font-display text-3xl md:text-4xl font-bold text-red-500 mb-4 uppercase">
            Edit Profile
          </h1>
          <p className="text-gray-400 mb-8">
            Update your profile information. Changes may take a moment to reflect on your public profile.
          </p>

          <form onSubmit={handleSubmit} className="bg-zinc-900 rounded-lg p-8 border border-red-900/20">
            {/* Current Images */}
            <div className="mb-6">
              <label className="flex items-center gap-2 text-sm font-mono text-gray-400 mb-2 tracking-wider uppercase">
                Current Images
              </label>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {model?.image_urls?.map((url, index) => (
                  <div key={index} className="aspect-square rounded-lg overflow-hidden border border-red-900/30">
                    <img 
                      src={`${BACKEND_URL}${url}`}
                      alt={`Current ${index + 1}`}
                      className="w-full h-full object-cover"
                    />
                  </div>
                ))}
              </div>
              <p className="text-gray-600 text-xs mt-2">
                {model?.image_urls?.length || 0}/4 images. Add more below if needed.
              </p>
            </div>

            {/* Add New Images */}
            {(model?.image_urls?.length || 0) + newImages.length < 4 && (
              <div className="mb-6">
                <label className="flex items-center gap-2 text-sm font-mono text-gray-400 mb-2 tracking-wider uppercase">
                  <Upload size={16} /> Add More Images
                </label>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  {newPreviews.map((preview, index) => (
                    <div key={index} className="relative aspect-square rounded-lg overflow-hidden border border-green-500/30">
                      <img 
                        src={preview} 
                        alt={`New ${index + 1}`}
                        className="w-full h-full object-cover"
                      />
                      <button
                        type="button"
                        onClick={() => removeNewImage(index)}
                        className="absolute top-2 right-2 w-6 h-6 bg-black/80 rounded-full flex items-center justify-center text-white hover:bg-red-600 transition-colors"
                      >
                        <X size={14} />
                      </button>
                      <span className="absolute bottom-2 left-2 bg-green-600 text-white text-xs px-2 py-1 rounded">New</span>
                    </div>
                  ))}
                  
                  <label className="aspect-square rounded-lg border-2 border-dashed border-red-900/30 hover:border-red-500 transition-colors cursor-pointer flex flex-col items-center justify-center text-gray-500 hover:text-red-400">
                    <Upload size={24} className="mb-2" />
                    <span className="text-xs text-center px-2">Add Image</span>
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleImageChange}
                      className="hidden"
                      multiple
                    />
                  </label>
                </div>
              </div>
            )}

            {/* Name */}
            <div className="mb-6">
              <label className="flex items-center gap-2 text-sm font-mono text-gray-400 mb-2 tracking-wider uppercase">
                <User size={16} /> Name
              </label>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleChange}
                className="w-full bg-black border border-red-900/30 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-red-500 transition-colors"
              />
            </div>

            {/* Instagram */}
            <div className="mb-6">
              <label className="flex items-center gap-2 text-sm font-mono text-gray-400 mb-2 tracking-wider uppercase">
                <Instagram size={16} /> Instagram URL
              </label>
              <input
                type="url"
                name="instagram_url"
                value={formData.instagram_url}
                onChange={handleChange}
                placeholder="https://instagram.com/yourprofile"
                className="w-full bg-black border border-red-900/30 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-red-500 transition-colors"
              />
            </div>

            {/* Bio */}
            <div className="mb-6">
              <label className="flex items-center gap-2 text-sm font-mono text-gray-400 mb-2 tracking-wider uppercase">
                <FileText size={16} /> Bio
              </label>
              <textarea
                name="bio"
                value={formData.bio}
                onChange={handleChange}
                rows={4}
                className="w-full bg-black border border-red-900/30 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-red-500 transition-colors resize-none"
              />
            </div>

            {/* Categories */}
            <div className="mb-8">
              <label className="flex items-center gap-2 text-sm font-mono text-gray-400 mb-2 tracking-wider uppercase">
                <Tag size={16} /> Categories
              </label>
              <div className="flex flex-wrap gap-2">
                {availableCategories.map((category) => (
                  <button
                    key={category}
                    type="button"
                    onClick={() => toggleCategory(category)}
                    className={`px-4 py-2 rounded-full text-sm transition-all ${
                      selectedCategories.includes(category)
                        ? 'bg-red-600 text-white'
                        : 'bg-zinc-800 text-gray-400 hover:bg-zinc-700 hover:text-white'
                    }`}
                  >
                    {category}
                  </button>
                ))}
              </div>
            </div>

            {/* Save Button */}
            <button
              type="submit"
              disabled={saving}
              className="w-full bg-red-600 hover:bg-red-700 disabled:bg-red-900 disabled:cursor-not-allowed text-white py-4 rounded-full font-mono text-sm tracking-widest uppercase transition-all flex items-center justify-center gap-2"
            >
              {saving ? 'Saving...' : (
                <>
                  <Save size={18} /> Save Changes
                </>
              )}
            </button>
          </form>

          {/* Public Profile Link */}
          {model?.status === 'approved' && (
            <div className="mt-6 flex flex-col sm:flex-row items-center justify-center gap-4">
              <a 
                href={`/models/${modelId}`}
                className="text-red-400 hover:text-red-300 transition-colors text-sm"
              >
                View your public profile →
              </a>
              <Link 
                to={`/models/${modelId}/analytics/${editToken}`}
                className="inline-flex items-center gap-2 bg-zinc-800 hover:bg-zinc-700 text-white px-4 py-2 rounded-full transition-colors text-sm"
                data-testid="view-analytics-btn"
              >
                <BarChart3 size={16} /> View Analytics
              </Link>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ModelEdit;
